import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import VariableModal from '../VariableModal/VariableModal'
import TaskModal, { TASK_TYPE_PROMPT, TASK_TYPE_WEBHOOK, TASK_TYPE_IFRAME } from './TaskModal'
import {
    discoverExternalToolsForIframeLink,
    updatePreConfigTask,
    uploadNewPreConfigTask,
} from '../../../../utils/backends/Assistants/assistantsFirestore'
import { CONFIRM_POPUP_TRIGGER_DELETE_PRE_CONFIG_TASK } from '../../ConfirmPopup'
import { showConfirmPopup } from '../../../../redux/actions'
import {
    getAssistantInProjectObject,
    getGlobalAssistant,
    isGlobalAssistant,
    GLOBAL_PROJECT_ID,
} from '../../../AdminPanel/Assistants/assistantsHelper'
import { RECURRENCE_NEVER } from '../../../TaskListView/Utils/TasksHelper'
import {
    INHERIT_ASSISTANT_MODEL,
    getPreConfigTaskModelSelection,
} from '../../../../functions/Assistant/preConfigTaskModel'
import {
    getPreConfigTaskReasoningEffortSelection,
    getPreConfigTaskReasoningEffortValue,
} from '../../../../functions/Assistant/preConfigTaskReasoningEffort'
import {
    TASK_EXECUTION_MODE_DIRECT,
    TASK_EXECUTION_MODE_WORKFLOW,
    getTaskExecutionMode,
} from '../../../../utils/taskExecutionMode'
import { getCurrentUserTaskRecurrence } from './taskRecurrence'

export default function PreConfigTaskModal({ disabled, projectId, closeModal, adding, assistantId, task }) {
    const dispatch = useDispatch()
    const loggedUser = useSelector(state => state.loggedUser)
    const selectedProjectIndex = useSelector(state => state.selectedProjectIndex)
    const loggedUserProjects = useSelector(state => state.loggedUserProjects)
    const selectedProjectId = loggedUserProjects[selectedProjectIndex]?.id
    const currentProjectId = projectId === GLOBAL_PROJECT_ID && selectedProjectId ? selectedProjectId : projectId
    const currentAssistant = useMemo(
        () =>
            adding
                ? isGlobalAssistant(assistantId)
                    ? getGlobalAssistant(assistantId)
                    : getAssistantInProjectObject(projectId, assistantId)
                : null,
        [adding, assistantId, projectId]
    )

    const initialState = useMemo(() => {
        const getInitialStartDate = () => {
            if (task?.startDate) {
                // If startDate is already a timestamp, use it
                if (typeof task.startDate === 'number') {
                    return task.startDate
                }
                // If we have a date string and time string, combine them
                const date = moment(task.startDate)
                if (task.startTime) {
                    const [hours, minutes] = task.startTime.split(':').map(Number)
                    date.hour(hours).minute(minutes)
                }
                return date.valueOf()
            }
            // For new tasks, default to tomorrow at 9 AM
            return moment().add(1, 'day').startOf('day').hour(9).minute(0).valueOf()
        }

        return {
            name: task ? task.name : '',
            prompt: task ? task.prompt : '',
            link: task ? task.link : '',
            taskType: task ? task.type : TASK_TYPE_PROMPT,
            variables: task ? task.variables : [],
            aiModel: task ? getPreConfigTaskModelSelection(task) : INHERIT_ASSISTANT_MODEL,
            aiReasoningEffort: getPreConfigTaskReasoningEffortSelection(task),
            aiSystemMessage: task ? task.aiSystemMessage : currentAssistant ? currentAssistant.instructions : '',
            recurrence: task ? getCurrentUserTaskRecurrence(task, loggedUser.uid) : RECURRENCE_NEVER,
            executionMode: task ? getTaskExecutionMode(task, TASK_EXECUTION_MODE_DIRECT) : TASK_EXECUTION_MODE_WORKFLOW,
            startDate: getInitialStartDate(),
            sendWhatsApp: task?.sendWhatsApp ?? false,
            webhookUrl: task?.taskMetadata?.webhookUrl ?? '',
            webhookAuthHeaderName: task?.taskMetadata?.webhookAuthHeaderName ?? 'Authorization',
            webhookAuth: task?.taskMetadata?.webhookAuth ?? '',
        }
    }, [task, currentAssistant, loggedUser.uid])

    const [name, setName] = useState(initialState.name)
    const [prompt, setPrompt] = useState(initialState.prompt)
    const [link, setLink] = useState(initialState.link)
    const [taskType, setTaskType] = useState(initialState.taskType)
    const [variables, setVariables] = useState(initialState.variables)
    const [activeVariableIndex, setActiveVariableIndex] = useState(null)
    const [showVariableModal, setShowVariableModal] = useState(false)
    const [aiModel, setAiModel] = useState(initialState.aiModel)
    const [aiReasoningEffort, setAiReasoningEffort] = useState(initialState.aiReasoningEffort)
    const [aiSystemMessage, setAiSystemMessage] = useState(initialState.aiSystemMessage)
    const [recurrence, setRecurrence] = useState(initialState.recurrence)
    const [executionMode, setExecutionMode] = useState(initialState.executionMode)
    const [startDate, setStartDate] = useState(initialState.startDate)
    const [sendWhatsApp, setSendWhatsApp] = useState(initialState.sendWhatsApp)
    const getInitialIframeDiscoveryStatus = () => {
        const integration =
            task?.taskMetadata?.externalIntegration && typeof task.taskMetadata.externalIntegration === 'object'
                ? task.taskMetadata.externalIntegration
                : {}
        const toolsCount = Array.isArray(integration.tools) ? integration.tools.length : 0
        const error = typeof integration.lastDiscoveryError === 'string' ? integration.lastDiscoveryError : ''
        return {
            loading: false,
            toolsCount,
            error,
        }
    }
    const [iframeDiscoveryStatus, setIframeDiscoveryStatus] = useState(getInitialIframeDiscoveryStatus)
    const externalIntegrationDetails =
        task?.taskMetadata?.externalIntegration && typeof task.taskMetadata.externalIntegration === 'object'
            ? task.taskMetadata.externalIntegration
            : null

    const applyCurrentUserRecurrenceConfig = baseTask => {
        const currentUserId = loggedUser.uid
        const taskWithActivation = { ...baseTask }
        const currentActivatedUserIds = Array.isArray(taskWithActivation.activatedUserIds)
            ? taskWithActivation.activatedUserIds.filter(Boolean)
            : []
        let nextActivatedUserIds = [...new Set(currentActivatedUserIds)]

        const currentRecurrenceByUser =
            taskWithActivation.recurrenceByUser && typeof taskWithActivation.recurrenceByUser === 'object'
                ? taskWithActivation.recurrenceByUser
                : {}
        const nextRecurrenceByUser = { ...currentRecurrenceByUser }

        if (recurrence && recurrence !== RECURRENCE_NEVER) {
            nextRecurrenceByUser[currentUserId] = recurrence
            nextActivatedUserIds = [...new Set([...nextActivatedUserIds, currentUserId])]
        } else {
            delete nextRecurrenceByUser[currentUserId]
            nextActivatedUserIds = nextActivatedUserIds.filter(userId => userId !== currentUserId)
        }

        return {
            ...taskWithActivation,
            creatorUserId: taskWithActivation.creatorUserId || currentUserId,
            activatorUserId: currentUserId,
            activatedUserIds: nextActivatedUserIds,
            recurrenceByUser: nextRecurrenceByUser,
        }
    }
    const [webhookUrl, setWebhookUrl] = useState(initialState.webhookUrl)
    const [webhookAuthHeaderName, setWebhookAuthHeaderName] = useState(initialState.webhookAuthHeaderName)
    const [webhookAuth, setWebhookAuth] = useState(initialState.webhookAuth)

    const handleSetStartDate = useCallback(value => {
        setStartDate(value)
    }, [])

    const promptInputRef = useRef()
    const linkInputRef = useRef()

    const handleSetRecurrence = useCallback(value => {
        setRecurrence(value)
    }, [])

    const handleSetName = useCallback(value => {
        setName(value)
    }, [])

    const getIframeTaskMetadata = async existingTaskMetadata => {
        const baseMetadata =
            existingTaskMetadata && typeof existingTaskMetadata === 'object' ? { ...existingTaskMetadata } : {}
        const cleanLink = typeof link === 'string' ? link.trim() : ''
        const discoveredAt = Date.now()

        if (!cleanLink) return baseMetadata

        const currentIntegration =
            baseMetadata.externalIntegration && typeof baseMetadata.externalIntegration === 'object'
                ? { ...baseMetadata.externalIntegration }
                : {}

        setIframeDiscoveryStatus({
            loading: true,
            toolsCount: Array.isArray(currentIntegration.tools) ? currentIntegration.tools.length : 0,
            error: '',
        })

        const discovered = await discoverExternalToolsForIframeLink(cleanLink)
        const hasDiscoveredTools =
            discovered?.success && Array.isArray(discovered?.tools) && discovered.tools.length > 0

        baseMetadata.externalIntegration = {
            ...currentIntegration,
            sourceUrl: cleanLink,
            discoveredAt,
            ...(hasDiscoveredTools
                ? {
                      version: discovered.version || currentIntegration.version || '1.0',
                      integrationId: discovered.integrationId || currentIntegration.integrationId || '',
                      integrationName: discovered.integrationName || currentIntegration.integrationName || '',
                      origin: discovered.origin || currentIntegration.origin || '',
                      manifestUrl: discovered.manifestUrl || currentIntegration.manifestUrl || '',
                      tools: discovered.tools,
                      lastDiscoveryError: '',
                  }
                : {
                      tools: Array.isArray(currentIntegration.tools) ? currentIntegration.tools : [],
                      lastDiscoveryError: discovered?.error || 'Could not discover external tools',
                  }),
            discoveryAttempts: Array.isArray(discovered?.attempts) ? discovered.attempts : [],
        }

        setIframeDiscoveryStatus({
            loading: false,
            toolsCount: hasDiscoveredTools ? discovered.tools.length : 0,
            error: hasDiscoveredTools ? '' : discovered?.error || 'Could not discover external tools',
        })

        return baseMetadata
    }

    const addTask = async () => {
        closeModal()

        // Convert startDate to UTC by removing the local timezone offset
        const utcStartDate = moment(startDate).utc().valueOf()
        const iframeTaskMetadata = taskType === TASK_TYPE_IFRAME ? await getIframeTaskMetadata() : null
        const aiModelOverride = aiModel === INHERIT_ASSISTANT_MODEL ? null : aiModel
        const aiReasoningEffortOverride = getPreConfigTaskReasoningEffortValue(aiReasoningEffort)

        const newTask =
            taskType === TASK_TYPE_PROMPT
                ? {
                      name,
                      type: taskType,
                      prompt,
                      variables,
                      link: '',
                      ...(aiModelOverride ? { aiModelOverride } : {}),
                      ...(aiReasoningEffortOverride !== undefined
                          ? { aiReasoningEffort: aiReasoningEffortOverride }
                          : {}),
                      aiSystemMessage: aiSystemMessage || currentAssistant?.instructions || '',
                      recurrence,
                      executionMode,
                      startDate: utcStartDate,
                      startTime: moment(startDate).format('HH:mm'),
                      userTimezone: parseInt(moment().format('Z')), // Store user's timezone offset
                      creatorUserId: loggedUser.uid, // Store the creator's user ID
                      activatorUserId: loggedUser.uid,
                      activatedUserIds: [loggedUser.uid],
                      activatedInProjectId: currentProjectId,
                      sendWhatsApp,
                  }
                : taskType === TASK_TYPE_WEBHOOK
                  ? {
                        name,
                        type: taskType,
                        prompt: '',
                        variables: [],
                        link: '',
                        recurrence,
                        executionMode,
                        sendWhatsApp,
                        taskMetadata: {
                            isWebhookTask: true,
                            webhookUrl: webhookUrl.trim(),
                            ...(webhookAuthHeaderName && webhookAuthHeaderName.trim()
                                ? { webhookAuthHeaderName: webhookAuthHeaderName.trim() }
                                : { webhookAuthHeaderName: 'Authorization' }),
                            ...(webhookAuth && webhookAuth.trim() ? { webhookAuth: webhookAuth.trim() } : {}),
                        },
                    }
                  : taskType === TASK_TYPE_IFRAME
                    ? {
                          name,
                          type: taskType,
                          prompt: '',
                          variables: [],
                          link,
                          taskMetadata: iframeTaskMetadata || {},
                          recurrence,
                          executionMode,
                          sendWhatsApp,
                      }
                    : { name, type: taskType, prompt: '', variables: [], link, recurrence, executionMode, sendWhatsApp }
        setTimeout(() => {
            uploadNewPreConfigTask(projectId, assistantId, applyCurrentUserRecurrenceConfig(newTask))
        }, 1000)
    }

    const saveTask = async () => {
        // Convert startDate to UTC by removing the local timezone offset
        const utcStartDate = moment(startDate).utc().valueOf()
        const iframeTaskMetadata =
            taskType === TASK_TYPE_IFRAME ? await getIframeTaskMetadata(task?.taskMetadata || {}) : null
        const aiModelOverride = aiModel === INHERIT_ASSISTANT_MODEL ? null : aiModel
        const aiReasoningEffortOverride = getPreConfigTaskReasoningEffortValue(aiReasoningEffort)

        const updatedTask =
            taskType === TASK_TYPE_PROMPT
                ? {
                      ...task,
                      name,
                      type: taskType,
                      prompt,
                      variables,
                      link: '',
                      aiModelOverride,
                      aiReasoningEffort: aiReasoningEffortOverride,
                      aiSystemMessage: aiSystemMessage || currentAssistant?.instructions || '',
                      recurrence: recurrence ?? null,
                      executionMode,
                      startDate: utcStartDate,
                      startTime: moment(startDate).format('HH:mm'),
                      userTimezone: parseInt(moment().format('Z')), // Store user's timezone offset
                      creatorUserId: task.creatorUserId || loggedUser.uid,
                      activatorUserId: task.activatorUserId || loggedUser.uid,
                      activatedUserIds:
                          task.activatedUserIds && task.activatedUserIds.length > 0
                              ? task.activatedUserIds
                              : [task.activatorUserId || task.creatorUserId || loggedUser.uid],
                      activatedInProjectId: currentProjectId,
                      sendWhatsApp,
                  }
                : taskType === TASK_TYPE_WEBHOOK
                  ? {
                        ...task,
                        name,
                        type: taskType,
                        prompt: '',
                        variables: [],
                        link: '',
                        recurrence: recurrence ?? null,
                        executionMode,
                        sendWhatsApp,
                        taskMetadata: {
                            isWebhookTask: true,
                            webhookUrl: webhookUrl.trim(),
                            ...(webhookAuthHeaderName && webhookAuthHeaderName.trim()
                                ? { webhookAuthHeaderName: webhookAuthHeaderName.trim() }
                                : { webhookAuthHeaderName: 'Authorization' }),
                            ...(webhookAuth && webhookAuth.trim() ? { webhookAuth: webhookAuth.trim() } : {}),
                        },
                    }
                  : taskType === TASK_TYPE_IFRAME
                    ? {
                          ...task,
                          name,
                          type: taskType,
                          prompt: '',
                          variables: [],
                          link,
                          taskMetadata: iframeTaskMetadata || {},
                          recurrence: recurrence ?? null,
                          executionMode,
                          sendWhatsApp,
                      }
                    : {
                          ...task,
                          name,
                          type: taskType,
                          prompt: '',
                          variables: [],
                          link,
                          recurrence: recurrence ?? null,
                          executionMode,
                          sendWhatsApp,
                      }
        updatePreConfigTask(projectId, assistantId, applyCurrentUserRecurrenceConfig(updatedTask))
        closeModal()
    }

    const deleteTask = () => {
        dispatch(
            showConfirmPopup({
                trigger: CONFIRM_POPUP_TRIGGER_DELETE_PRE_CONFIG_TASK,
                object: {
                    taskId: task.id,
                    projectId: projectId,
                    assistantId: assistantId,
                },
            })
        )
        closeModal()
    }

    const addVariable = variable => {
        setVariables(state => [...state, variable])
        setTimeout(() => {
            const newPrompt = prompt.trim() + ` $${variable.name}`
            const editor = promptInputRef.current.getEditor()
            editor.setText(newPrompt, 'user')
        })
    }

    const removeVariable = variableIndex => {
        setVariables(state => state.toSpliced(variableIndex, 1))
        const newPrompt = prompt.trim().replaceAll(`$${variables[variableIndex].name}`, ``)
        promptInputRef.current.getEditor().setText(newPrompt, 'user')
    }

    const updateVariable = (variable, variableIndex) => {
        setVariables(state => {
            const newState = [...state]
            newState[variableIndex] = variable
            return newState
        })
        setTimeout(() => {
            const newPrompt = prompt.trim().replaceAll(`$${variables[variableIndex].name}`, `$${variable.name}`)
            promptInputRef.current.getEditor().setText(newPrompt, 'user')
        })
    }

    const openVariableModal = variableIndex => {
        setShowVariableModal(true)
        setActiveVariableIndex(variableIndex)
    }

    const closeVariableModal = () => {
        setShowVariableModal(false)
        setActiveVariableIndex(null)
    }

    return (
        <View>
            {showVariableModal ? (
                <VariableModal
                    closeModal={closeVariableModal}
                    addVariable={addVariable}
                    updateVariable={updateVariable}
                    variableIndex={activeVariableIndex}
                    variables={variables}
                    disabled={disabled}
                />
            ) : (
                <TaskModal
                    closeModal={closeModal}
                    adding={adding}
                    name={name}
                    setName={handleSetName}
                    prompt={prompt}
                    setPrompt={setPrompt}
                    variables={variables}
                    openVariableModal={openVariableModal}
                    removeVariable={removeVariable}
                    addTask={addTask}
                    saveTask={saveTask}
                    deleteTask={deleteTask}
                    disabled={disabled}
                    promptInputRef={promptInputRef}
                    linkInputRef={linkInputRef}
                    link={link}
                    setLink={setLink}
                    taskType={taskType}
                    setTaskType={setTaskType}
                    aiModel={aiModel}
                    setAiModel={setAiModel}
                    aiReasoningEffort={aiReasoningEffort}
                    setAiReasoningEffort={setAiReasoningEffort}
                    aiSystemMessage={aiSystemMessage}
                    setAiSystemMessage={setAiSystemMessage}
                    projectId={projectId}
                    promptProjectId={currentProjectId}
                    assistantId={assistantId}
                    recurrence={recurrence}
                    setRecurrence={handleSetRecurrence}
                    executionMode={executionMode}
                    setExecutionMode={setExecutionMode}
                    startDate={startDate}
                    setStartDate={handleSetStartDate}
                    sendWhatsApp={sendWhatsApp}
                    setSendWhatsApp={setSendWhatsApp}
                    webhookUrl={webhookUrl}
                    setWebhookUrl={setWebhookUrl}
                    webhookAuthHeaderName={webhookAuthHeaderName}
                    setWebhookAuthHeaderName={setWebhookAuthHeaderName}
                    webhookAuth={webhookAuth}
                    setWebhookAuth={setWebhookAuth}
                    iframeDiscoveryStatus={iframeDiscoveryStatus}
                    externalIntegrationDetails={externalIntegrationDetails}
                />
            )}
        </View>
    )
}
